import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, type QuotasReport, type UsageSummary } from "@/lib/api";
import {
  hardQuotaBadgeLabel,
  isHardQuotaEnforced,
  quotaMetricRows,
  utilizationBarClass,
} from "@/lib/quotasDisplay";
import { ErrorBox, PageHeader, Spinner, StatTile } from "@/components/ui";
import { Badge } from "@/components/ui/badge";

export function UsagePage() {
  const { id = "" } = useParams<{ id: string }>();
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [quotas, setQuotas] = useState<QuotasReport | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const [u, q] = await Promise.all([api.usage(id), api.usageQuotas(id)]);
        setUsage(u.data);
        setQuotas(q.data);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const maxApi = useMemo(() => {
    if (!usage?.buckets?.length) return 1;
    return Math.max(1, ...usage.buckets.map((b) => b.api_requests));
  }, [usage]);

  if (loading) return <Spinner label="Loading usage…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Telemetry"
        title="Usage & Quotas"
        description={
          usage
            ? `${new Date(usage.from).toLocaleString()} → ${new Date(usage.to).toLocaleString()}`
            : "Hourly aggregates and current billing-cycle quotas"
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      <div className="mb-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile label="API requests" value={usage?.api_requests ?? 0} />
        <StatTile label="DB queries" value={usage?.database_queries ?? 0} />
        <StatTile label="Rows read" value={usage?.rows_read ?? 0} />
        <StatTile label="Rows written" value={usage?.rows_written ?? 0} />
        <StatTile label="Errors" value={usage?.error_count ?? 0} />
      </div>

      {quotas ? (
        <div className="card mb-6 p-5" data-testid="hard-quotas-panel">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-sans text-sm font-semibold text-text-primary">
              Current cycle quotas
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant={isHardQuotaEnforced(quotas) ? "default" : "secondary"}
                data-testid="quota-enforced-badge"
              >
                {hardQuotaBadgeLabel(quotas)}
              </Badge>
              <span className="mono-chip">
                {new Date(quotas.cycle_start).toLocaleDateString()} →{" "}
                {new Date(quotas.cycle_end).toLocaleDateString()}
              </span>
            </div>
          </div>
          <p className="mb-1 text-xs text-text-muted">{quotas.note}</p>
          {isHardQuotaEnforced(quotas) ? (
            <p className="mb-3 text-xs text-text-muted">
              Hard plan limits are enforced on Data API traffic when a metric
              exceeds its cap (noisy-neighbor protection). Soft metrics may still
              appear for advisory visibility.
            </p>
          ) : (
            <p className="mb-3 text-xs text-text-muted">
              Limits are advisory for this report response.
            </p>
          )}
          <div className="space-y-2">
            {quotaMetricRows(quotas).map((m) => (
              <div key={m.name}>
                <div className="mb-0.5 flex justify-between text-xs">
                  <span className="font-mono text-text-muted">
                    {m.name}
                    {m.soft ? (
                      <span className="ml-1 text-[10px] opacity-70">soft</span>
                    ) : isHardQuotaEnforced(quotas) ? (
                      <span className="ml-1 text-[10px] opacity-70">hard</span>
                    ) : null}
                  </span>
                  <span className="tabular-nums">
                    {m.used.toLocaleString()} / {m.limit.toLocaleString()} (
                    {m.utilization_pct.toFixed(1)}%)
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-surface-container-low">
                  <div
                    className={`h-full rounded-full ${utilizationBarClass(m.utilization_pct, isHardQuotaEnforced(quotas))}`}
                    style={{ width: `${Math.min(100, m.utilization_pct)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="card p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-sans text-sm font-semibold text-text-primary">
            API requests by hour
          </h2>
          <span className="mono-chip">hourly buckets</span>
        </div>
        {!usage || usage.buckets.length === 0 ? (
          <p className="text-sm text-text-muted">
            No usage buckets yet. Counters appear as traffic is recorded.
          </p>
        ) : (
          <div className="flex h-44 items-end gap-1 rounded-lg border border-border-standard bg-surface-container-low/80 px-2 pb-2 pt-4">
            {usage.buckets.map((b) => {
              const h = Math.max(6, Math.round((b.api_requests / maxApi) * 100));
              return (
                <div
                  key={b.bucket_start}
                  className="group relative flex-1 rounded-t-sm bg-primary/80 transition-colors duration-fast ease-standard hover:bg-primary"
                  style={{ height: `${h}%` }}
                  title={`${new Date(b.bucket_start).toLocaleString()}: ${b.api_requests} req`}
                >
                  <span className="pointer-events-none absolute -top-7 left-1/2 z-tooltip hidden -translate-x-1/2 whitespace-nowrap rounded-sm bg-inverse-surface px-1.5 py-0.5 font-mono text-[10px] text-inverse-on-surface group-hover:block">
                    {b.api_requests}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
